import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { api } from "../../config/api"; // our interceptor instance

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    location: "",
    is_active: true
  });

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/branches");
      setBranches(res.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch branches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleShowAdd = () => {
    setModalMode("add");
    setFormData({ id: null, name: "", location: "", is_active: true });
    setShowModal(true);
    setError("");
  };

  const handleShowEdit = (branch) => {
    setModalMode("edit");
    setFormData({ 
      id: branch.id, 
      name: branch.name, 
      location: branch.location || "", 
      is_active: branch.is_active === 1 || branch.is_active === true
    });
    setShowModal(true);
    setError("");
  };

  const confirmDelete = (branch) => {
    setBranchToDelete(branch);
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    if (!branchToDelete) return;
    try {
      await api.delete(`/api/branches/${branchToDelete.id}`);
      setShowConfirmModal(false);
      setBranchToDelete(null);
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete branch");
      setShowConfirmModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        name: formData.name,
        location: formData.location,
        is_active: formData.is_active
      };

      if (modalMode === "add") {
        await api.post("/api/branches", payload);
      } else {
        await api.put(`/api/branches/${formData.id}`, payload);
      }
      setShowModal(false);
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const textDarkBlueStyle = { color: "#1e3c72" };
  const darkBlueStyle = { backgroundColor: "#1e3c72", color: "white", borderColor: "#1e3c72" };

  return (
    <Container fluid className="py-4 px-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1" style={textDarkBlueStyle}>Branches</h2>
          <p className="text-muted mb-0">Manage all branches and locations</p>
        </div>
        <Button style={darkBlueStyle} className="shadow-sm rounded d-flex align-items-center" onClick={handleShowAdd}>
          <FaPlus className="me-2" /> Add Branch
        </Button>
      </div>

      {error && <div className="alert alert-danger shadow-sm border-0">{error}</div>}

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="border-0 px-4 py-3 text-muted fw-semibold">ID</th>
                <th className="border-0 px-4 py-3 text-muted fw-semibold">Name</th>
                <th className="border-0 px-4 py-3 text-muted fw-semibold">Location</th>
                <th className="border-0 px-4 py-3 text-muted fw-semibold">Status</th>
                <th className="border-0 px-4 py-3 text-muted fw-semibold text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-5 text-muted">Loading branches...</td></tr>
              ) : branches.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-5 text-muted">No branches found. Click 'Add Branch' to create one.</td></tr>
              ) : (
                branches.map(branch => (
                  <tr key={branch.id}>
                    <td className="px-4 py-3">#{branch.id}</td>
                    <td className="px-4 py-3 fw-bold" style={textDarkBlueStyle}>{branch.name}</td>
                    <td className="px-4 py-3 text-muted">{branch.location || "-"}</td>
                    <td className="px-4 py-3">
                      {branch.is_active ? (
                        <Badge bg="success" className="px-2 py-1 rounded-pill fw-normal">Active</Badge>
                      ) : (
                        <Badge bg="secondary" className="px-2 py-1 rounded-pill fw-normal">Inactive</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <Button variant="light" size="sm" className="me-2 text-primary shadow-sm" onClick={() => handleShowEdit(branch)}>
                        <FaEdit />
                      </Button>
                      <Button variant="light" size="sm" className="text-danger shadow-sm" onClick={() => confirmDelete(branch)} disabled={branch.id === 1}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold" style={textDarkBlueStyle}>
              {modalMode === "add" ? "Add New Branch" : "Edit Branch"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium text-muted">Branch Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g. HQ Branch, Siam Square"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium text-muted">Location / Description</Form.Label>
              <Form.Control
                type="text"
                value={formData.location}
                onChange={e => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Bangkok, Thailand"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="is-active-switch"
                label="Branch is Active"
                checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" style={darkBlueStyle} className="px-4 shadow-sm">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <div className="mb-3 text-danger"><FaTrash size={40} /></div>
          <h5 className="fw-bold mb-2">Delete Branch?</h5>
          <p className="text-muted small mb-4">
            Are you sure you want to delete branch <strong>{branchToDelete?.name}</strong>? This action cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="outline-secondary" className="px-4" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
            <Button variant="danger" className="px-4 shadow-sm" onClick={handleDelete}>Delete</Button>
          </div>
        </Modal.Body>
      </Modal>

    </Container>
  );
}
